import {Plugin, PluginKey} from '@milkdown/prose/state';
import {Decoration, DecorationSet} from '@milkdown/prose/view';
import type {MarkType} from '@milkdown/prose/model';
import {exists} from 'shared/utils/custom-utils';

interface PlaceholderPluginOptions {
    textPlaceholder: string;
    headingPlaceholder: string;
    quotePlaceholder: string;
    codeBlockPlaceholder: string;
}

export function createPlaceholderPlugin(options: PlaceholderPluginOptions) {
    const key = new PluginKey('placeholder-plugin');

    return new Plugin({
        key,
        props: {
            decorations(state) {
                const {doc, selection, storedMarks, schema} = state;
                const {$from, empty} = selection;

                if (!empty) {
                    return null;
                }

                const parent = $from.parent;
                if (parent.content.size > 0) return null;

                const grand = $from.node($from.depth - 1);
                const parentType = parent.type.name;
                const grandType = grand?.type.name;

                let placeholderText: string;
                if (parentType === 'code_block') {
                    placeholderText = options.codeBlockPlaceholder;
                } else if (parentType === 'paragraph' && grandType === 'blockquote') {
                    placeholderText = options.quotePlaceholder;
                } else if (parentType === 'heading') {
                    placeholderText = options.headingPlaceholder;
                } else {
                    placeholderText = options.textPlaceholder;
                }

                const effectiveMarks = storedMarks ?? $from.marks();
                const markTypes = effectiveMarks.map((m) => m.type);

                const strong = schema.marks.strong;
                const em = schema.marks.em || schema.marks.emphasis;
                const strike = Object.values(schema.marks).find((markType) =>
                    markType.spec.parseDOM?.some((rule) => {
                        if ('style' in rule && typeof (rule as any).style === 'string') {
                            return (rule as any).style.includes('line-through');
                        }
                        return false;
                    }) ||
                    markType.name.toLowerCase().includes('strike') ||
                    markType.name.toLowerCase().includes('delete') ||
                    markType.name.toLowerCase() === 's'
                );

                const has = (type: MarkType) => exists(type) && markTypes.some((m) => m === type);

                const isBold = has(strong);
                const isItalic = has(em);
                const isStrike = has(strike);

                const span = document.createElement('span');
                span.className = 'milkdown-placeholder';
                span.textContent = placeholderText;
                if (isBold) span.classList.add('placeholder-bold');
                if (isItalic) span.classList.add('placeholder-italic');
                if (isStrike) span.classList.add('placeholder-strike');

                const pos = $from.start();
                const deco = Decoration.widget(pos, () => span, {side: -1});

                return DecorationSet.create(doc, [deco]);
            },
        },
    });
}